/**
 * Centralized Error Handler
 * Captures and logs errors with context, implements retry logic for dynamic imports
 */

interface ErrorContext {
    timestamp: string;
    userAgent: string;
    url: string;
    online: boolean;
    screen: {
        width: number;
        height: number;
    };
    language: string;
    timezone: string;
}

interface ErrorLog {
    message: string;
    stack?: string;
    context: ErrorContext;
    errorType: 'runtime' | 'promise' | 'import' | 'component';
    component?: string;
    userId?: string;
    userEmail?: string;
    breadcrumbs?: Breadcrumb[];
    status?: 'new' | 'investigating' | 'resolved';
}

interface Breadcrumb {
    message: string;
    category: string;
    timestamp: string;
    data?: any;
}

/**
 * Breadcrumbs utility to track user actions
 */
class Breadcrumbs {
    private static instance: Breadcrumbs;
    private items: Breadcrumb[] = [];
    private readonly MAX_ITEMS = 20;

    private constructor() {
        // Auto-capture some global events
        this.initializeAutoCapture();
    }

    public static getInstance(): Breadcrumbs {
        if (!Breadcrumbs.instance) {
            Breadcrumbs.instance = new Breadcrumbs();
        }
        return Breadcrumbs.instance;
    }

    public add(message: string, category: string = 'manual', data?: any) {
        this.items.push({
            message,
            category,
            data,
            timestamp: new Date().toISOString()
        });
        if (this.items.length > this.MAX_ITEMS) {
            this.items.shift();
        }
    }

    public get(): Breadcrumb[] {
        return [...this.items];
    }

    private initializeAutoCapture() {
        // Navigation changes
        window.addEventListener('popstate', () => {
            this.add(`Navigate to ${window.location.pathname}`, 'navigation');
        });

        // Clicks
        window.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            if (target) {
                const label = target.innerText?.slice(0, 30) || target.id || target.tagName;
                this.add(`Click: ${label}`, 'ui');
            }
        }, true);
    }
}

const breadcrumbs = Breadcrumbs.getInstance();

/**
 * Get error context information
 */
function getErrorContext(): ErrorContext {
    return {
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        online: navigator.onLine,
        screen: {
            width: window.screen.width,
            height: window.screen.height,
        },
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
}

/**
 * Log error to console with formatting
 */
function logErrorToConsole(errorLog: ErrorLog) {
    console.group('🚨 Application Error');
    console.error('Message:', errorLog.message);
    if (errorLog.stack) {
        console.error('Stack:', errorLog.stack);
    }
    console.log('Type:', errorLog.errorType);
    console.log('Context:', errorLog.context);
    if (errorLog.breadcrumbs) {
        console.log('Breadcrumbs:', errorLog.breadcrumbs);
    }
    console.groupEnd();
}

/**
 * Send error to backend/Firebase for logging
 */
async function logErrorToBackend(errorLog: ErrorLog) {
    try {
        const { getFirestore, collection, addDoc, serverTimestamp } = await import('firebase/firestore');
        const db = getFirestore();

        // Ensure we don't have undefined breadcrumbs for Firestore
        const logData = {
            ...errorLog,
            status: errorLog.status || 'new',
            createdAt: serverTimestamp(),
            notificationSent: false,
        };

        // Deep clean undefined values (Firestore doesn't like them)
        const cleanLog = JSON.parse(JSON.stringify(logData));

        // Re-inject the serverTimestamp since JSON.stringify strips it
        cleanLog.createdAt = serverTimestamp();

        await addDoc(collection(db, 'error_logs'), cleanLog);
        console.log('Error logged to Firebase');
    } catch (e) {
        console.error('Failed to log error to backend:', e);
    }
}

/**
 * Retry dynamic import with exponential backoff
 */
export async function retryDynamicImport<T>(
    importFn: () => Promise<T>,
    maxRetries = 3,
    baseDelay = 1000
): Promise<T> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
            return await importFn();
        } catch (error) {
            lastError = error as Error;

            if (error instanceof Error && !error.message.includes('fetch')) {
                throw error;
            }

            if (attempt < maxRetries - 1) {
                const delay = baseDelay * Math.pow(2, attempt);
                console.warn(`Import failed, retrying in ${delay}ms... (attempt ${attempt + 1}/${maxRetries})`);
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
    }

    const { auth } = await import('./firebase');
    const user = auth.currentUser;

    const errorLog: ErrorLog = {
        message: `Dynamic import failed after ${maxRetries} attempts: ${lastError?.message}`,
        stack: lastError?.stack,
        context: getErrorContext(),
        errorType: 'import',
        userId: user?.uid,
        userEmail: user?.email || undefined,
        breadcrumbs: breadcrumbs.get(),
    };

    logErrorToConsole(errorLog);
    await logErrorToBackend(errorLog);

    throw lastError;
}

/**
 * Main error handler function
 */
export async function handleError(
    error: Error,
    errorType: ErrorLog['errorType'] = 'runtime',
    component?: string,
    userId?: string
) {
    // Try to get dynamic user info if not provided
    let finalUserId = userId;
    let userEmail: string | undefined = undefined;

    try {
        const { auth } = await import('./firebase');
        const user = auth.currentUser;
        if (user) {
            finalUserId = user.uid;
            userEmail = user.email || undefined;
        }
    } catch (e) {
        // Auth might not be loaded yet
    }

    const errorLog: ErrorLog = {
        message: error.message,
        stack: error.stack,
        context: getErrorContext(),
        errorType,
        component,
        userId: finalUserId,
        userEmail,
        breadcrumbs: breadcrumbs.get(),
        status: 'new'
    };

    logErrorToConsole(errorLog);
    logErrorToBackend(errorLog).catch(console.error);
}

/**
 * Initialize global error handlers
 */
export function initializeErrorHandlers() {
    // Catch uncaught errors
    window.addEventListener('error', (event) => {
        handleError(
            new Error(event.message),
            'runtime'
        );
    });

    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
        const error = event.reason instanceof Error
            ? event.reason
            : new Error(String(event.reason));

        handleError(error, 'promise');
    });

    console.log('✅ Error handlers initialized');
}

/**
 * Show user-friendly error message
 */
export function showUserError(message: string, technical?: string) {
    // You can integrate this with your toast/notification system
    console.error('User Error:', message);
    if (technical) {
        console.error('Technical details:', technical);
    }

    // For now, use alert - replace with your UI notification system
    alert(`Error: ${message}\n\nPlease try refreshing the page. If the problem persists, contact support.`);
}
